/** Lesson session page — creates or resumes a learn session for a course lesson. */
import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useCourseEnrolment,
  useCourseProgress,
  useCreateReviseSession,
  useRevisableContent,
} from '@/shared/api/hooks';
import { useSessionGuard } from '@/shared/hooks/useSessionGuard';
import { useSessionLauncher, SessionPlayer, Interstitial } from '.';
import LoadingIcon from '@/foundation/ui/LoadingIcon';

/**
 * Route component for `/courses/:courseId/:lessonId`.
 *
 * Keys LessonSessionContent on lessonId + retryKey so that:
 *  - Navigating to a different lesson forces a full remount (resets useSessionLauncher state)
 *  - Retrying after failure also forces a full remount
 */
export default function LessonSessionPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const [retryKey, setRetryKey] = useState(0);
  const handleRetry = useCallback(() => setRetryKey((k) => k + 1), []);

  return <LessonSessionContent key={`${lessonId}-${retryKey}`} onRetry={handleRetry} />;
}

function LessonSessionContent({ onRetry }: { onRetry: () => void }) {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const { guardSession } = useSessionGuard();

  const coursePath = `/courses/${courseId}`;

  // ── Enrollment & progression checks ──────────────────────────────────────

  const { data: enrolment, isLoading: checkingEnrolment } = useCourseEnrolment(courseId);
  const isEnrolled = enrolment != null;

  const { data: progressData, isFetching: fetchingProgress } = useCourseProgress(
    isEnrolled ? courseId : undefined
  );
  const lessonProgress = progressData?.lessons.find((l) => l.lessonId === lessonId);
  const passPct = lessonProgress?.passPct ?? null;

  // Only treat as locked when progress data is fresh (not mid-refetch).
  // During a background refetch stale cached data might still show the lesson
  // as locked even though completing the previous lesson just unlocked it.
  const isLessonLocked = !fetchingProgress && lessonProgress?.status === 'locked';

  // ── Session launcher ───────────────────────────────────────────────────

  const onExit = useCallback(() => navigate(coursePath), [navigate, coursePath]);

  const {
    session,
    sessionReady,
    loading,
    createError,
    submitFn,
    sessionType,
    startIndex,
    sortedSteps,
    confidencePositions,
    paasPositions,
    handleComplete,
    handleExit,
  } = useSessionLauncher({
    lessonId,
    courseId,
    enabled: isEnrolled && !isLessonLocked,
    onExit,
  });

  // ── Next lesson ──────────────────────────────────────────────────────────

  const handleNextLesson = useCallback(() => {
    if (!progressData || !lessonId || !courseId) return;
    const lessons = progressData.lessons;
    const currentIdx = lessons.findIndex((l) => l.lessonId === lessonId);
    if (currentIdx === -1 || currentIdx >= lessons.length - 1) return;
    const nextLesson = lessons[currentIdx + 1];
    guardSession({
      lessonId: nextLesson.lessonId,
      onProceed: () => navigate(`/courses/${courseId}/${nextLesson.lessonId}`),
    });
  }, [progressData, lessonId, courseId, navigate, guardSession]);

  // Check if a next lesson exists
  const hasNextLesson = useMemo(() => {
    if (!progressData || !lessonId) return false;
    const lessons = progressData.lessons;
    const currentIdx = lessons.findIndex((l) => l.lessonId === lessonId);
    return currentIdx !== -1 && currentIdx < lessons.length - 1;
  }, [progressData, lessonId]);

  // ── Revise this lesson ───────────────────────────────────────────────────
  // The revisable content cache is invalidated in useStepSubmit when the backend
  // auto-completes the session, so hasRevisionContent will reactively become true
  // once the refetch returns the newly-revisable lesson.

  const { data: revisableData } = useRevisableContent();
  const createRevise = useCreateReviseSession();

  const hasRevisionContent = useMemo(() => {
    if (!revisableData || !lessonId) return false;
    for (const course of revisableData.courses) {
      for (const mod of course.modules) {
        for (const topic of mod.topics) {
          for (const lesson of topic.lessons) {
            if (lesson.id === lessonId && lesson.reviseStepCount > 0) return true;
          }
        }
      }
    }
    return false;
  }, [revisableData, lessonId]);

  const handleReviseLesson = useCallback(() => {
    if (!lessonId) return;
    guardSession({
      onProceed: () => {
        createRevise.mutate(
          { lessonIds: [lessonId], maxQuestions: 20 },
          {
            onSuccess: () => navigate('/revision/session', { state: { newSession: true } }),
            onError: () => toast.error('No revision content available for this lesson yet'),
          }
        );
      },
    });
  }, [lessonId, guardSession, createRevise, navigate]);

  // ── Render states (priority order) ─────────────────────────────────────

  if (checkingEnrolment) {
    return (
      <div
        className="flex min-h-dvh items-start justify-center pt-[28vh]"
        style={{ background: 'var(--color-bg)' }}
      >
        <LoadingIcon size="lg" />
      </div>
    );
  }

  if (!isEnrolled) {
    return (
      <Interstitial
        title="Not enrolled"
        description="You must be enrolled in this course to access its lessons."
        primaryLabel="Go to course"
        onPrimary={() => navigate(coursePath)}
      />
    );
  }

  // Show loading while progress is being refetched (avoids brief "locked" flash
  // when navigating from a completion screen that just unlocked this lesson).
  if (fetchingProgress && (!lessonProgress || lessonProgress.status === 'locked')) {
    return (
      <div
        className="flex min-h-dvh items-start justify-center pt-[28vh]"
        style={{ background: 'var(--color-bg)' }}
      >
        <LoadingIcon size="lg" />
      </div>
    );
  }

  if (isLessonLocked) {
    return (
      <Interstitial
        title="Lesson locked"
        description="Complete the prerequisite lessons first to unlock this one."
        primaryLabel="Back to course"
        onPrimary={() => navigate(coursePath)}
      />
    );
  }

  if (loading) {
    return (
      <div
        className="flex min-h-dvh items-start justify-center pt-[28vh]"
        style={{ background: 'var(--color-bg)' }}
      >
        <LoadingIcon size="lg" />
      </div>
    );
  }

  if (!sessionReady) {
    return (
      <Interstitial
        title={createError ? 'Couldn\u2019t start the session' : 'No active session'}
        description={
          createError
            ? 'The lesson may still be generating. Head back and try again in a moment.'
            : 'Something went wrong loading this lesson.'
        }
        primaryLabel="Back to course"
        onPrimary={() => navigate(coursePath)}
      />
    );
  }

  // ── Render session player ──────────────────────────────────────────────

  return (
    <SessionPlayer
      key={session!.id}
      sessionId={session!.id}
      steps={sortedSteps}
      mode={sessionType}
      startIndex={startIndex}
      confidencePositions={confidencePositions}
      paasPositions={paasPositions}
      onExit={handleExit}
      onComplete={handleComplete}
      submitFn={submitFn}
      onNextLesson={hasNextLesson ? handleNextLesson : undefined}
      onRetry={onRetry}
      onReviseLesson={hasRevisionContent ? handleReviseLesson : undefined}
      revisingLesson={createRevise.isPending}
      passPct={passPct}
    />
  );
}
